import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScriptableContext
} from 'chart.js';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

// --- Chart Registration ---
ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// --- Icons (Inline SVGs for performance) ---
const SearchIcon = () => (
  <svg
    className="w-5 h-5 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);
const TrendingUpIcon = () => (
  <svg
    className="w-5 h-5 text-green-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);
const HomeIcon = () => (
  <svg
    className="w-5 h-5 text-blue-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

// --- Interfaces ---
interface SaleRecord {
  address: string;
  price: number;
  date: string;
  isNewBuild: boolean;
  county: string;
}

// --- Helper: Currency Formatter ---
const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);

// --- Component: No Data State ---
const NoSalesData: React.FC<{ area?: string }> = ({ area }) => (
  <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
    <div className="bg-white p-4 rounded-full shadow-sm mb-4">
      <svg
        width="40"
        height="40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        className="text-gray-400"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      No recent data for {area ? area.toUpperCase() : 'this area'}
    </h3>
    <p className="text-gray-500 mb-6 text-center max-w-sm px-4">
      We haven't tracked sales here recently. However, our valuation engine uses
      wider market trends.
    </p>
    <a
      href="/home-valuation"
      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm"
    >
      Get a Valuation Instead
    </a>
  </div>
);

// --- Component: Loading Skeleton ---
const SkeletonLoader = () => (
  <div className="animate-pulse space-y-6">
    <div className="h-32 bg-gray-200 rounded-xl w-full"></div>
    <div className="h-64 bg-gray-200 rounded-xl w-full"></div>
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-16 bg-gray-100 rounded-lg w-full"></div>
      ))}
    </div>
  </div>
);

// --- Main Page Component ---
const PriceTrendsPage: React.FC = () => {
  const { area } = useParams<{ area: string }>();
  const navigate = useNavigate();
  const [sales, setSales] = useState<SaleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // 1. Fetch Data
  useEffect(() => {
    if (!area) {
      setLoading(false);
      return;
    }
    const fetchSales = async () => {
      setLoading(true);
      try {
        const resp = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getRecentSalesByArea?area=${encodeURIComponent(area)}`
        );
        const data = await resp.json();
        // Sort by date ascending for chart correctness initially
        const sorted = (data.sales || []).sort(
          (a: SaleRecord, b: SaleRecord) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        setSales(sorted);
      } catch (err) {
        console.error('Failed to fetch sales', err);
        setSales([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSales();
  }, [area]);

  // 2. Computed Statistics
  const stats = useMemo(() => {
    if (!sales.length) return null;
    const prices = sales.map((s) => s.price);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    const max = Math.max(...prices);
    const min = Math.min(...prices);
    return { avg, max, min, count: sales.length };
  }, [sales]);

  // 3. Filter Logic
  const filteredSales = useMemo(() => {
    // Reverse sort for the list (newest first)
    const list = [...sales].reverse();
    if (!search) return list;
    const lowerSearch = search.toLowerCase();
    return list.filter(
      (s) =>
        s.address.toLowerCase().includes(lowerSearch) || s.date.includes(search)
    );
  }, [sales, search]);

  // 4. Chart Configuration
  const chartData = {
    labels: sales.map((s) => s.date), // Already sorted ascending
    datasets: [
      {
        label: 'Sale Price',
        data: sales.map((s) => s.price),
        fill: true,
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.5)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)');
          return gradient;
        },
        borderColor: '#2563EB', // Blue-600
        pointBackgroundColor: '#fff',
        pointBorderColor: '#2563EB',
        pointHoverBackgroundColor: '#2563EB',
        pointHoverBorderColor: '#fff',
        tension: 0.4, // Smooth curve
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#F3F4F6',
        bodyColor: '#F3F4F6',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context: any) => formatCurrency(context.raw)
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        grid: { color: '#F3F4F6' },
        ticks: {
          callback: (value: any) => {
            if (value >= 1000000)
              return '€' + (value / 1000000).toFixed(1) + 'm';
            if (value >= 1000) return '€' + (value / 1000).toFixed(0) + 'k';
            return value;
          },
          font: { size: 11 },
          color: '#6B7280'
        }
      },
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11 },
          color: '#6B7280',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6
        }
      }
    }
  };

const county = area ? routingKeyToCounty[area.toUpperCase()] : undefined;
const areaName = area?.toUpperCase();
const currentYear = new Date().getFullYear();

  return (
    <>
      <Helmet>
        {/* Primary Title - Targets District + Area + Intent */}
        <title>{`Property Prices in ${areaName}, ${county} | ${currentYear} Market Report`}</title>

        {/* Meta Description - Uses "Sales Prices" and "Valuation" keywords */}
        <meta
          name="description"
          content={`Explore real-time property prices in ${areaName} (${county}). Average house price: ${stats ? formatCurrency(stats.avg) : 'view report'}. Get a free property valuation and track ${currentYear} market trends today.`}
        />

        {/* Keywords - While less weighted, they help define the page context */}
        <meta
          name="keywords"
          content={`property prices ${areaName}, house prices ${county}, ${areaName} market report, real estate ${areaName}, property valuation Ireland`}
        />

        {/* Canonical Link */}
        <link
          rel="canonical"
          href={`https://rentsmart.ie/prices/${areaName?.toLowerCase()}`}
        />

        {/* Open Graph / Social Media Tags */}
        <meta
          property="og:title"
          content={`Market Report: Property Prices in ${areaName}`}
        />
        <meta
          property="og:description"
          content={`What is a house worth in ${areaName}? See the latest sold prices and market trends for ${currentYear}.`}
        />
        <meta
          property="og:url"
          content={`https://rentsmart.ie/prices/${area}`}
        />
        <meta property="og:type" content="website" />

        {/* Structured Data (JSON-LD) for Local SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Report',
            name: `Property Market Report for ${areaName}`,
            description: `Current property sales data and market analysis for ${areaName}, ${county}.`,
            datePublished: new Date().toISOString(),
            author: {
              '@type': 'Organization',
              name: 'RentSmart Ireland'
            }
          })}
        </script>
      </Helmet>

      <div className="bg-gray-50 min-h-screen pb-12">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200 pt-8 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <span>Market Data</span>
                  <span>/</span>
                  <span className="font-medium text-gray-900">
                    {county}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Property Market:{' '}
                  <span className="text-blue-600">{county}</span>
                  {area && (
                    <span className="text-gray-400 font-normal ml-2 text-2xl">
                      ({area?.toUpperCase()})
                    </span>
                  )}
                </h1>
                <p className="mt-2 text-gray-600 max-w-2xl">
                  Real-time registry data and market trends. Updated daily.
                </p>
              </div>

              {/* Desktop High-Level CTA */}
              <div className="hidden md:block">
                <button
                  onClick={() => navigate('/home-valuation')}
                  className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition shadow-sm flex items-center gap-2"
                >
                  <HomeIcon />
                  Value my home
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 -mt-6">
          {loading ? (
            <SkeletonLoader />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* LEFT COLUMN: Data & Charts (Span 2) */}
              <div className="lg:col-span-2 space-y-8">
                {/* Stats Grid */}
                {stats && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Avg Price
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(stats.avg)}
                      </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Highest
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(stats.max)}
                      </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Volume
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.count}
                        </p>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center">
                          <TrendingUpIcon /> Recent
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chart Section */}
                {sales.length > 0 && (
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900 mb-6">
                      Price Trend History
                    </h2>
                    <div className="h-72 w-full">
                      <Line data={chartData} options={chartOptions} />
                    </div>
                  </div>
                )}

                {/* Sales List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      Recent Transactions
                    </h2>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon />
                      </div>
                      <input
                        type="text"
                        placeholder="Search address..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64 transition"
                      />
                    </div>
                  </div>

                  {filteredSales.length === 0 ? (
                    <div className="p-4">
                      <NoSalesData area={area} />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                          <tr>
                            <th className="px-6 py-4">Date</th>
                            <th className="px-6 py-4">Address</th>
                            <th className="px-6 py-4 text-right">Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredSales.map((sale, idx) => (
                            <tr
                              key={idx}
                              className="hover:bg-gray-50 transition duration-150"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {sale.date}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {sale.address}
                                  </span>
                                  {sale.isNewBuild && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 uppercase tracking-wide">
                                      New
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right font-bold text-gray-900">
                                {formatCurrency(sale.price)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN: Conversion & CTA (Sticky) */}
              <div className="lg:col-span-1">
                <div className="sticky top-6 space-y-6">
                  {/* Valuation CTA Card */}
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl"></div>

                    <h3 className="text-xl font-bold mb-2 relative z-10">
                      Selling in {county}?
                    </h3>
                    <p className="text-blue-100 mb-6 text-sm relative z-10">
                      Property prices are changing. Get an instant, data-backed
                      valuation report for your home today.
                    </p>

                    <button
                      onClick={() => navigate('/home-valuation')}
                      className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold py-3 px-4 rounded-xl shadow transition transform active:scale-95 flex items-center justify-center gap-2"
                    >
                      Get Free Valuation
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>

                    <div className="mt-4 flex items-center justify-center gap-4 text-xs text-blue-200 opacity-80">
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Instant
                      </span>
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Free
                      </span>
                    </div>
                  </div>

                  {/* Secondary Insights Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h4 className="font-bold text-gray-900 mb-3">
                      Market Insight
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      The average price in <strong>{county}</strong> is trending
                      {stats && stats.avg > 400000 ? ' higher' : ' steady'}{' '}
                      compared to the county average.
                    </p>
                    <div className="border-t border-gray-100 pt-4 mt-4">
                      <div className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2">
                        Sources
                      </div>
                      <div className="flex gap-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          PPR Register
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PriceTrendsPage;
